import React from "react";
import {
  CheckCircleIcon,
  ChevronDownIcon,
  ClockIcon,
  PaintBrushIcon,
  SparklesIcon,
  TagIcon,
} from "@heroicons/react/24/outline";

const appName = process.env.REACT_APP_NAME || "Vehicle";
const appVersion = process.env.REACT_APP_VER || "0.0.0";

const latestUpdateResume = "securise les sauvegardes, les comptes, les uploads, les pieces jointes privees et les reponses API sensibles.";
const updates = [
  {
    version: "1.24.0",
    title: "Premier lot complet de correctifs securite",
    date: "11 aout 2026",
    sections: [
      {
        title: "Sauvegardes SQL privees",
        items: [
          "Deplacement des sauvegardes de uploads/BDD vers le dossier prive backend/BDD, en dehors de toute racine servie par Fastify.",
          "Application automatique des permissions 0700 au dossier et 0600 aux dumps afin de reserver leur acces au compte proprietaire et a root.",
          "Retrait complet de l'ancienne racine uploads du serveur statique : une ancienne URL /uploads/BDD retourne desormais 404.",
          "Mise a jour de la sauvegarde manuelle et de son interface pour utiliser et afficher uniquement le nouvel emplacement prive.",
        ],
      },
      {
        title: "Suppression securisee des comptes",
        items: [
          "Ajout de la saisie et de la verification du mot de passe actuel avant toute suppression de compte.",
          "Ajout du grade Deleted, identifie par le GradeID 7 et volontairement depourvu de tout droit.",
          "Ajout de gradeDeleted pour conserver le grade precedent dans les listes et le journal apres la suppression.",
          "Remplacement du mot de passe par une valeur aleatoire inconnue, anonymisation du surnom, de l'email et de la photo, puis passage a l'etat Supprime.",
          "Application du meme grade sans droit et de la meme revocation aux membres professionnels supprimes.",
        ],
      },
      {
        title: "Sessions et autorisations",
        items: [
          "Ajout de TokenVersion dans la base et dans les JWT afin de revoquer durablement une session lors d'une suppression ou d'un changement d'etat.",
          "Verification centralisee de l'existence et de l'etat actif de l'utilisateur pour tous les grades sur chaque route authentifiee.",
          "Maintien de la revocation d'un ancien JWT meme si un compte bloque est reactive ulterieurement.",
          "Interdiction de modifier son propre etat, de modifier un SuperAdmin ou de reactiver un compte deja supprime depuis /users/change-etat.",
          "Suppression des secrets dans les reponses de changement d'etat et controle explicite de l'etat actif dans les gardes administratifs.",
        ],
      },
      {
        title: "SEC-03 · Administration cloisonnee",
        items: [
          "Interdiction pour un administrateur de se bloquer lui-meme ou de bloquer, reactiver ou modifier l'etat d'un SuperAdmin.",
          "Verification systematique de l'etat actif dans les gardes de tous les grades et revocation des sessions lors des changements sensibles.",
          "Suppression des mots de passe haches et des sels dans les reponses de changement d'etat et de mise a jour du profil.",
        ],
      },
      {
        title: "SEC-04 · Validation forte des fichiers",
        items: [
          "Restriction des photos aux formats JPEG, PNG et WebP et des pieces jointes aux memes images ou aux PDF.",
          "Detection du format par signature binaire reelle, sans faire confiance au type MIME ni a l'extension envoyes par le navigateur.",
          "Generation de noms aleatoires cote serveur afin de neutraliser les noms de fichiers malveillants et les collisions.",
        ],
      },
      {
        title: "SEC-05 · Quotas dynamiques et anti-saturation",
        items: [
          "Ajout du droit administrable AttachmentMaxFileMb : 5 Mo pour Utilisateur, 15 Mo pour Premium et 200 Mo pour Professionnel et Membre professionnel.",
          "Conservation du quota de nombre AttachmentMaxPerItem, dont la limite d'un fichier par entretien ou releve pour l'offre gratuite.",
          "Remplacement de la limite globale de 50 Go et des copies completes en memoire par un flux progressif vers un fichier temporaire prive, avec plafond absolu de 200 Mo.",
          "Diffusion progressive de l'export ZIP d'un vehicule afin qu'un dossier contenant de gros devis ne soit plus charge integralement en memoire.",
          "Correction de la reponse asynchrone Fastify afin que le ZIP progressif soit entierement transmis au navigateur au lieu d'un fichier vide.",
          "Affichage et controle immediat de la taille autorisee dans les formulaires de photos, entretiens et releves.",
        ],
      },
      {
        title: "SEC-06 · Stockage public et prive separes",
        items: [
          "Creation d'une racine statique dediee aux seules photos publiques validees et retrait complet de backend/uploads du serveur statique.",
          "Stockage des devis, factures, PDF et justificatifs dans private-uploads, accessible uniquement par les routes API authentifiees et controlees par compte.",
          "Migration automatique au demarrage des anciennes photos valides vers la racine publique et des anciens dossiers d'entretiens ou de releves vers la racine privee en mode 0600.",
          "Mise a niveau de @fastify/static vers la version corrigee 10.1.3 et ajout des en-tetes nosniff et CSP exclusivement sur les images publiques, sans sandboxer l'interface React.",
          "Correction du chargement en production des anciennes photos dont le nom contient des espaces ou des caracteres reserves, sans reexposer les pieces jointes privees.",
          "Mise a niveau de Fastify, Multipart, bcrypt et node-cron, retrait des dependances serveur inutilisees et audit npm de production ramene a zero vulnerabilite signalee.",
        ],
      },
      {
        title: "SEC-07 · Donnees sensibles et erreurs API",
        items: [
          "Suppression des JWT, objets utilisateurs complets, mots de passe haches et sels dans les journaux et les reponses publiques identifies par l'audit.",
          "Remplacement des details d'erreurs internes par des messages generiques pour ne plus exposer la structure technique du serveur.",
          "Revocation de toutes les sessions existantes lors d'un changement de mot de passe.",
        ],
      },
      {
        title: "Administration et listes",
        items: [
          "Affichage du grade d'origine dans les listes des comptes particuliers, administrateurs et professionnels supprimes.",
          "Ajout du mot de passe actuel dans la confirmation finale de la zone de suppression du compte avec retour d'erreur visible.",
          "Migration des comptes deja supprimes vers Deleted avec conservation de leur ancien grade et revocation de leurs JWT historiques.",
        ],
      },
      {
        title: "Migration et validation",
        items: [
          "Ajout de la migration Prisma secure_deleted_accounts pour creer Deleted, gradeDeleted et TokenVersion sans appliquer automatiquement la migration en production.",
          "Ajout de la migration secure_upload_limits pour enregistrer les limites 5, 15 et 200 Mo dans le moteur de droits.",
          "Ajout de tests de securite couvrant les comptes inactifs, les sessions revoquees, le mot de passe obligatoire, le SuperAdmin et les listes de comptes supprimes.",
          "Validation du schema Prisma, des 85 tests backend, des 29 tests frontend, du build de production et du controle git diff.",
        ],
      },
    ],
  },
  {
    version: "1.23.0",
    title: "Anomalies, entretiens automatiques et progression des offres",
    date: "10 aout 2026",
    sections: [
      {
        title: "Droits des garages et immobilisations",
        items: [
          "Application du droit EntretienCreate a la gestion des fiches garage et a la creation d'une immobilisation, cote interface comme cote serveur.",
          "Gestion des fiches reservee au compte professionnel principal disposant du droit requis, avec consultation maintenue pour les membres supplementaires.",
          "Application de EntretienUpdate pour cloturer toutes les immobilisations et de EntretienUpdateOwn pour limiter un membre a ses propres signalements.",
          "Passage automatique des garages et immobilisations en lecture seule lorsque l'essai ou l'abonnement ne permet plus l'ecriture.",
          "Ajout de messages explicites selon que le blocage provient du grade, du statut de membre supplementaire ou de l'expiration du compte.",
        ],
      },
      {
        title: "Historique et kilometrage",
        items: [
          "Ajout du tri croissant ou decroissant de l'historique des entretiens par date, vehicule, entretien, garage, note, fichiers et cout.",
          "Ajout des filtres par date, recherche de vehicule, type d'entretien, garage et contenu de la note, avec compteurs et pagination recalcules.",
          "Reinitialisation des filtres lors d'un changement de perimetre et affichage d'un etat vide specifique lorsqu'aucun resultat ne correspond.",
          "Respect de Shield Mode et du mode confidentialite en masquant les filtres, valeurs et tris susceptibles d'exposer des donnees protegees.",
          "Ajout d'un choix entre le kilometrage cumule et la distance parcourue entre chaque releve dans le graphique du vehicule.",
        ],
      },
      {
        title: "Anomalies de consommation",
        items: [
          "Detection apres trois consommations reelles de reference d'une surconsommation superieure a 20 pour cent ou d'une sous-consommation inferieure a moins 20 pour cent par rapport a la mediane du vehicule.",
          "Priorite donnee a l'alerte de valeur potentiellement erronee lorsque l'ecart absolu depasse 30 pour cent.",
          "Creation d'une notification pour le compte principal et pour l'auteur du releve concerne, sans notifier les autres membres du compte.",
          "Ajout d'une croix pour masquer chaque anomalie et d'un lien direct vers le vehicule concerne depuis la cloche de navigation.",
          "Journalisation dedupliquee de chaque anomalie : une detection deja presente dans le journal n'est pas recreee, tandis qu'une anomalie corrigee est resolue.",
          "Resynchronisation des alertes apres la creation, la modification ou la suppression d'un releve et nettoyage lors de la suppression du vehicule.",
        ],
      },
      {
        title: "Planification automatique des entretiens",
        items: [
          "Ajout de regles generales recurrentes par type d'entretien avec un delai en mois, un intervalle kilometrique ou les deux, sans valeur prechargee.",
          "Ajout de la gestion de ces regles generales dans les donnees de reference de l'administration, avec activation et desactivation individuelles.",
          "Creation d'une proposition d'entretien depuis le dernier entretien realise du meme type, en utilisant son kilometrage ou le dernier releve disponible a cette date.",
          "Ajout d'un badge Automatique sur les entretiens concernes et remplacement propre des anciennes propositions encore en attente.",
          "Envoi de la proposition au compte principal et a l'auteur de l'entretien, avec les boutons Confirmer et Annuler directement dans la notification.",
          "Resolution des notifications et mise a jour des statuts lorsqu'une proposition est traitee, remplacee ou lorsque son entretien source est supprime.",
        ],
      },
      {
        title: "Personnalisation et moteur des droits",
        items: [
          "Ajout d'une personnalisation des regles par vehicule et type d'entretien, avec date, kilometrage ou combinaison des deux facultative.",
          "Ajout de la desactivation d'une regle personnalisee et du retour en un clic a la regle generale.",
          "Reservation de la personnalisation aux comptes Professionnel et Membre professionnel, tandis que Premium utilise uniquement les regles generales.",
          "Ajout du droit MaintenanceAutoPlanning, desactive par defaut pour Utilisateur et active pour les autres grades.",
          "Ajout du droit MaintenanceRuleCustomize, active pour l'administration et les comptes professionnels, avec blocage des comptes sans ecriture.",
          "Journalisation de la creation, de la confirmation, de l'annulation et de la personnalisation des planifications automatiques.",
        ],
      },
      {
        title: "Comparateur des offres",
        items: [
          "Ajout des droits de planification automatique et de personnalisation des regles dans le catalogue et le tableau public des offres.",
          "Harmonisation des variables nulles ou egales a zero avec l'affichage en croix Non des autres droits indisponibles.",
          "Conservation des valeurs actives particulieres comme Gratuit pour un prix nul et Illimite pour une limite negative.",
          "Nouveau tri progressif par colonne Utilisateur, Premium, Professionnel puis Membre professionnel, avec la priorite variable, Inclus puis Non a chaque palier.",
          "Regroupement visuel des fonctionnalites disponibles gratuitement, puis des apports successifs de Premium et de Professionnel.",
        ],
      },
      {
        title: "Donnees de test et validation",
        items: [
          "Ajout des tables Notification, RegleEntretienGenerale et RegleEntretienVehicule, des statuts automatiques et des cles de deduplication dans Prisma.",
          "Preparation de la migration des notifications et regles d'entretien sans execution automatique sur la base de donnees.",
          "Ajout de generateurs configurables de releves et d'entretiens de test pour un vehicule, avec plages aleatoires et graine reproductible optionnelle.",
          "Ajout de tests pour les droits des garages, les generateurs, les anomalies, les echeances automatiques, les filtres d'historique, le graphique kilometrique et le tri des offres.",
          "Validation des suites backend et frontend, du build de production et du controle git diff apres la mise a jour de version.",
        ],
      },
    ],
  },
  {
    version: "1.22.0",
    title: "Garages references et immobilisations des vehicules",
    date: "9 aout 2026",
    sections: [
      {
        title: "Carnet de garages",
        items: [
          "Ajout de la page Garages accessible depuis la navigation principale pour enregistrer les garages habituels du compte.",
          "Ajout des coordonnees structurees : nom, adresse, code postal, ville, telephone, email, raison sociale et SIRET.",
          "Ajout de la modification, de la desactivation et de la reactivation d'une fiche sans supprimer son historique.",
          "Partage automatique des garages entre le compte professionnel principal et ses membres supplementaires.",
          "Gestion des fiches reservee au compte professionnel principal, tandis que les membres peuvent utiliser les garages actifs.",
        ],
      },
      {
        title: "Garages dans les entretiens",
        items: [
          "Ajout d'un garage aux entretiens planifies comme aux entretiens realises.",
          "Ajout du choix entre une fiche garage enregistree et une saisie rapide sans creation de fiche.",
          "Conservation du nom du garage dans l'historique en complement de sa reference structuree.",
          "Compatibilite preservee avec les entretiens existants et leurs anciennes valeurs de garage en texte libre.",
          "Affichage du garage prevu dans le detail d'un entretien planifie et reprise automatique lors du passage en entretien realise.",
        ],
      },
      {
        title: "Immobilisations des vehicules",
        items: [
          "Ajout du signalement d'un vehicule indisponible avec garage enregistre ou libre, date de debut obligatoire et date de fin optionnelle.",
          "Conservation de l'acces et de la modification du vehicule pendant son immobilisation, le signalement restant purement informatif.",
          "Ajout de la remise en service avec historique de la date de fin et de l'utilisateur ayant cloture l'immobilisation.",
          "Autorisation pour un membre professionnel de creer une immobilisation et de cloturer uniquement celles qu'il a creees.",
          "Autorisation pour le compte professionnel principal de cloturer toutes les immobilisations de son organisation.",
        ],
      },
      {
        title: "Notifications, donnees et validation",
        items: [
          "Ajout des vehicules actuellement indisponibles dans la cloche de notifications de tous les membres du compte.",
          "Actualisation immediate de la notification apres une immobilisation ou une remise en service.",
          "Ajout des tables Garage et ImmobilisationVehicule avec CreateDate, relations de compte, createur et clotureur.",
          "Journalisation de l'ajout, de la modification et de la desactivation d'un garage ainsi que du debut et de la fin d'une immobilisation.",
          "Preparation de la migration des garages et immobilisations sans execution automatique sur la base de donnees.",
          "Validation du schema Prisma, des tests backend et frontend, du build de production et du controle git diff.",
        ],
      },
    ],
  },
  {
    version: "1.21.0",
    title: "Codes promotionnels, offres et simulation des couts",
    date: "9 aout 2026",
    sections: [
      {
        title: "Codes promotionnels configurables",
        items: [
          "Ajout des codes publics utilisables par tous dans une limite globale configurable et jusqu'a leur date d'expiration.",
          "Ajout des codes prives lies a un compte precis et utilisables une seule fois par ce compte.",
          "Ajout du ciblage configurable des abonnements mensuels, annuels ou des deux periodicites.",
          "Application de la remise promotionnelle sans cumul : elle remplace la reduction annuelle standard uniquement lorsqu'elle est plus avantageuse.",
        ],
      },
      {
        title: "Administration et suivi",
        items: [
          "Ajout d'un espace de creation et de suivi des codes promotionnels dans l'administration.",
          "Ajout d'une recherche par surnom, email, raison sociale ou SIRET et d'un select pour choisir le beneficiaire d'un code prive.",
          "Affichage des utilisations, de la limite, de l'expiration et du statut de chaque code, avec activation et desactivation manuelles.",
          "Journalisation de la creation, de la modification et de l'utilisation des codes promotionnels.",
        ],
      },
      {
        title: "Paiement factice et offres",
        items: [
          "Ajout de la verification d'un code et de l'aperçu du tarif reduit avant la simulation de paiement entierement factice.",
          "Reservation securisee d'une utilisation dans la meme operation que l'activation de l'abonnement simule.",
          "Correction de l'offre annuelle dans la page Abonnement avec le prix brut sur douze mois barre et le tarif remise affiche de maniere stable.",
          "Ajout de la page Offres dans la navigation de tous les comptes connectes.",
          "Conservation de la page publique pour les visiteurs, tandis qu'un compte connecte retrouve sa sidebar sans boutons de connexion ou d'inscription.",
        ],
      },
      {
        title: "Simulation des couts de roulage",
        items: [
          "Refonte complete de la page Simulation pour l'harmoniser avec les interfaces Abonnement et Entretien.",
          "Suppression des coefficients d'entretien fixes pour les voitures et les motos au profit des couts d'entretiens realises enregistres.",
          "Calcul du cout d'entretien au kilometre a partir des couts observes et de la distance reellement couverte par les donnees du vehicule.",
          "Ajout du mode Entretiens actifs par defaut, avec archives exclues et premier releve utilise comme borne de depart.",
          "Ajout du mode Historique complet, avec archives incluses et borne la plus ancienne entre le premier releve et le plus ancien entretien afin de ne jamais raccourcir la periode observee.",
          "Affichage explicite des donnees insuffisantes lorsqu'un kilometrage, un cout ou une borne de calcul exploitable manque.",
          "Conservation de la mediane des derniers intervalles de carburant et ajout du detail des sources utilisees pour chaque vehicule.",
        ],
      },
      {
        title: "Donnees et validation",
        items: [
          "Ajout des tables CodePromo et UtilisationCodePromo avec historique du prix avant et apres remise.",
          "Preparation de la migration des codes promotionnels sans execution automatique sur la base de donnees.",
          "Ajout de tests dedies aux calculs de carburant, aux deux perimetres d'entretien et aux cas de donnees insuffisantes.",
          "Validation des tests backend et frontend, du build de production et du controle git diff apres la refonte de Simulation.",
        ],
      },
    ],
  },
  {
    version: "1.20.0",
    title: "Tarifs, droits avances et fichiers de releve",
    date: "9 aout 2026",
    sections: [
      {
        title: "Tarifs et abonnements configurables",
        items: [
          "Ajout des tarifs mensuels configurables dans le moteur des droits : Utilisateur gratuit, Premium a 4,99 euros et Professionnel a 14,99 euros par mois.",
          "Ajout d'une reduction annuelle configurable, appliquee par defaut a 10 pour cent aux offres Premium et Professionnel.",
          "Ajout du choix mensuel ou annuel dans le paiement de demonstration avec calcul, enregistrement et affichage explicite du tarif entierement factice.",
          "Conservation du Membre professionnel comme extension incluse et rattachee a l'offre Professionnel, sans abonnement autonome.",
        ],
      },
      {
        title: "Nouveaux droits fonctionnels",
        items: [
          "Ajout du Journal d'activite avec les portees aucun, activite personnelle ou organisation selon le grade.",
          "Ajout et application du droit Comparaison de vehicules aux offres Premium et Professionnel.",
          "Ajout des Statistiques basiques pour tous les grades et des Statistiques avancees pour Premium et Professionnel.",
          "Clarification du droit de signalement d'une donnee a supprimer au responsable du compte.",
          "Ajout du quota configurable AttachmentMaxPerItem pour les pieces jointes d'entretien et de releve.",
        ],
      },
      {
        title: "Pieces jointes des releves",
        items: [
          "Ajout du modele ReleverFichier et du stockage securise des images et PDF dans le dossier du vehicule.",
          "Ajout de pieces jointes a la creation et a la modification d'un releve, avec categorie, nom et taille conserves.",
          "Ajout de la consultation authentifiee, de la suppression autorisee et du nettoyage physique lors de la suppression du fichier ou du releve.",
          "Application cote serveur des limites de 1 fichier pour Utilisateur, 3 pour Premium et 10 pour Professionnel et ses membres.",
          "Journalisation de l'ajout et de la suppression des fichiers de releve.",
        ],
      },
      {
        title: "Comparateur public et administration",
        items: [
          "Reorganisation du tableau /offres du moins d'options au plus complet, avec un ordre fonctionnel stable des lignes.",
          "Ajout des prix, de la remise annuelle, de la portee du journal, des statistiques, de la comparaison et des quotas de fichiers au comparateur public.",
          "Mise en evidence visuelle du Membre professionnel comme extension du compte Professionnel.",
          "Extension de la matrice d'administration aux tarifs en centimes, pourcentages, portees et nouvelles limites configurables.",
        ],
      },
      {
        title: "Validation",
        items: [
          "Ajout de tests pour les plages de prix, remises, portees du journal et quotas de pieces jointes.",
          "Validation du schema Prisma, des tests backend et frontend, du build de production et du controle git diff.",
          "Preparation de la migration des tarifs, nouveaux droits et fichiers de releve sans execution automatique sur la base de donnees.",
        ],
      },
    ],
  },
  {
    version: "1.19.0",
    title: "Comptes V2 et espaces professionnels",
    date: "6 aout 2026",
    sections: [
      {
        title: "Grades et droits personnalisables",
        items: [
          "Ajout des grades Premium, Professionnel et Professionnel supplementaire en complement du compte Utilisateur.",
          "Ajout d'une table de droits configurable par grade pour piloter les fonctionnalites et les limites sans modifier le code applicatif.",
          "Mise en place des limites VehiculeMaxActive, VehiculeMaxSold, ProMemberMax et TrialDurationDays pour les comptes personnels et professionnels.",
          "Separation des quotas de vehicules actifs et vendus, avec conservation du telechargement des donnees lorsqu'un vehicule devient inaccessible.",
        ],
      },
      {
        title: "Espace professionnel partage",
        items: [
          "Ajout d'un formulaire d'inscription Professionnel avec telephone, raison sociale et SIRET, ainsi qu'un essai complet de 7 jours configurable.",
          "Partage des vehicules, releves et entretiens entre le compte professionnel principal et ses comptes supplementaires.",
          "Attribution des droits complets de creation et suppression au compte principal, avec modification limitee a ses propres donnees pour un membre supplementaire.",
          "Ajout de la creation, du blocage et de la suppression des comptes professionnels supplementaires dans la limite configurable du compte.",
          "Transfert des releves et entretiens d'un membre supprime vers le compte principal en conservant la provenance de l'ancien membre.",
        ],
      },
      {
        title: "Abonnements et paiement de demonstration",
        items: [
          "Ajout de la base des abonnements Premium et Professionnel avec activation, renouvellement, annulation et expiration simules.",
          "Ajout de la table MoyenPaiement et d'un moyen Demo gratuite simulant une carte, une adresse, un telephone et les coordonnees du titulaire.",
          "Signalement explicite du caractere factice a chaque etape du parcours de paiement et d'abonnement actuel.",
          "Blocage des creations et modifications apres expiration de l'essai professionnel sans abonnement, tout en conservant les parametres, l'abonnement et les exports.",
          "Restriction automatique d'un compte Premium revenu au grade Utilisateur, sans suppression de ses vehicules excedentaires.",
        ],
      },
      {
        title: "Journal des actions et demandes de suppression",
        items: [
          "Ajout des tables Action et JournalAction avec nom, description, criticite, acteur, cible et details securises de chaque operation.",
          "Journalisation des creations, modifications, suppressions, blocages, vehicules, releves, entretiens, moyens de paiement et abonnements.",
          "Ajout d'un historique consultable par l'utilisateur pour suivre les operations realisees dans son espace partage.",
          "Ajout d'une demande de suppression pour les releves et entretiens des membres supplementaires, avec surbrillance et notification du compte principal.",
          "Uniformisation de CreateDate sur les tables persistantes concernees afin de conserver leur date de creation.",
        ],
      },
      {
        title: "Administration des comptes V2",
        items: [
          "Ajout d'une matrice d'administration du moteur des droits pour modifier les permissions et limites de chaque grade.",
          "Validation cote serveur des permissions 0/1, des valeurs illimitees et des plages autorisees pour chaque quota.",
          "Regroupement des comptes Utilisateur et Premium dans les listes de comptes particuliers avec affichage de leur grade.",
          "Ajout de listes dediees aux comptes professionnels actifs, bloques et supprimes.",
          "Affichage des comptes professionnels supplementaires directement dans la carte de leur compte principal.",
          "Blocage ou reactivation d'un espace professionnel applique a son compte principal et a tous ses membres non supprimes.",
          "Journalisation detaillee de chaque modification administrative apportee au moteur des droits.",
        ],
      },
      {
        title: "Comparateur public des offres",
        items: [
          "Ajout de la page publique /offres accessible sans connexion pour comparer les quatre grades de compte.",
          "Chargement dynamique du tableau depuis le moteur des droits afin de refleter automatiquement les changements administratifs.",
          "Affichage distinct des permissions, des limites et des quotas partages pour les comptes professionnels.",
          "Ajout de parcours d'inscription dedies aux particuliers et aux professionnels avec preselection du bon formulaire.",
          "Clarification du rattachement des comptes professionnels supplementaires et du caractere factice des paiements actuels.",
        ],
      },
      {
        title: "Corrections et robustesse",
        items: [
          "Correction des erreurs d'inscription afin d'identifier precisement les champs Surnom, Email ou SIRET deja utilises.",
          "Ajout d'une erreur sous le champ concerne, d'une mise en evidence visuelle accessible et d'un effacement lors de sa correction.",
          "Application de la meme detection aux administrateurs et aux comptes professionnels supplementaires, y compris lors d'un conflit concurrent en base.",
          "Correction du seed pour supprimer les declarations Honda et Suzuki en double et rendre les donnees de reference idempotentes.",
          "Fusion transactionnelle des doublons existants lors d'un prochain seed, apres reaffectation de toutes leurs relations.",
        ],
      },
      {
        title: "Validation",
        items: [
          "Ajout de tests backend pour les droits, les quotas, le journal d'actions et les erreurs precises de creation de compte.",
          "Ajout de tests frontend pour l'affichage et la correction des erreurs de champs du formulaire d'inscription.",
          "Validation du schema Prisma, des tests backend et frontend, du build de production et du controle git diff.",
          "Preparation des migrations V2 sans execution automatique sur la base de donnees.",
        ],
      },
    ],
  },
  {
    version: "1.18.0",
    title: "Filtres et couts d'entretien",
    date: "21 juin 2026",
    sections: [
      {
        title: "Filtre centralise",
        items: [
          "Ajout d'un filtre Perimetre en haut de la page Entretien pour basculer entre la vue globale et un vehicule precis.",
          "Application du meme perimetre aux cartes statistiques, a la section A venir prochainement, au calendrier, a la repartition des couts et a l'historique.",
          "Recalcul automatique des compteurs, des totaux et des paginations lorsque le perimetre change.",
        ],
      },
      {
        title: "Repartition des couts",
        items: [
          "Recalcul de la repartition des couts selon le vehicule selectionne ou la vue globale.",
          "Ajout d'un toggle Inclure archives pour comptabiliser les archives dans la repartition des couts sans changer les onglets d'historique.",
          "Ajout de barres de progression par categorie avec le pourcentage correspondant au total filtre.",
          "Mise a jour du donut et du total central selon le perimetre actif et l'option archives.",
        ],
      },
      {
        title: "Listes et historique",
        items: [
          "Filtrage de la section A venir prochainement par vehicule ou en vue globale.",
          "Filtrage des onglets Realises et Archives de l'historique selon le meme perimetre.",
          "Affichage du perimetre actif dans les sections concernees pour clarifier les valeurs affichees.",
        ],
      },
      {
        title: "Validation",
        items: [
          "Validation du build frontend apres l'ajout du filtre centralise, du toggle archives et des barres de progression.",
          "Conservation des warnings existants du projet sans ajout de nouveau warning lie aux changements.",
        ],
      },
    ],
  },
  {
    version: "1.17.0",
    title: "Notifications et archives d'entretien",
    date: "21 juin 2026",
    sections: [
      {
        title: "Notifications",
        items: [
          "Activation du bouton Notifications de la navbar pour afficher les entretiens planifies en retard.",
          "Ajout d'un badge compteur sur la cloche uniquement lorsqu'au moins un entretien est en retard.",
          "Ajout d'un menu de notifications avec le vehicule, le type d'entretien et l'echeance depassee.",
          "Ouverture directe de la page Entretien sur le detail de l'entretien concerne via un lien profond.",
        ],
      },
      {
        title: "Detail d'entretien",
        items: [
          "Ajout d'une modal d'information complete en lecture seule pour les entretiens planifies et realises.",
          "Ajout d'un bouton de passage vers la modification depuis la modal d'information.",
          "Ajout d'un bouton Marquer realise depuis la modal d'information d'un entretien planifie.",
          "Ajout d'un bouton Voir le detail dans les sections A venir prochainement et Historique.",
        ],
      },
      {
        title: "Archives",
        items: [
          "Ajout de l'option Archive ancien proprietaire sur les entretiens realises.",
          "Renommage de la section Historique recent en Historique.",
          "Ajout d'onglets Realises et Archives pour separer les entretiens de l'utilisateur et ceux des anciens proprietaires.",
          "Affichage d'un badge Archive sur les entretiens concernes dans l'historique et dans la modal de detail.",
        ],
      },
      {
        title: "Calculs et statistiques",
        items: [
          "Exclusion des archives des compteurs d'entretiens realises, du cout total et du detail des couts par categorie.",
          "Exclusion des archives de la page Statistiques globale afin de ne pas les comptabiliser dans les valeurs utilisateur.",
          "Conservation des archives dans l'historique du vehicule pour maintenir la trace des anciens entretiens.",
          "Ajout du champ EstArchive dans l'export CSV des entretiens realises.",
        ],
      },
      {
        title: "API et validation",
        items: [
          "Ajout du champ Prisma EstArchive sur EntretienRealise avec une migration dediee.",
          "Prise en charge de l'archive dans la creation, la modification et la completion d'un entretien.",
          "Validation du schema Prisma apres migration.",
          "Validation syntaxique des controleurs backend modifies et validation du build frontend.",
        ],
      },
    ],
  },
  {
    version: "1.16.0",
    title: "Page Statistiques globale",
    date: "19 juin 2026",
    sections: [
      {
        title: "Nouvelle page",
        items: [
          "Ajout de la page /statistiques dans le style sombre de la page Vehicule.",
          "Activation de l'entree Statistiques dans la navigation laterale et ajout des metadonnees de page.",
          "Ajout d'un toggle permettant d'inclure ou non les vehicules vendus dans le perimetre des statistiques.",
        ],
      },
      {
        title: "Kilometrage",
        items: [
          "Ajout d'un graphique Kilometrage avec une ligne distincte par vehicule pour comparer les evolutions.",
          "Correction du tri chronologique global afin que les releves recents ne soient plus places avant des releves plus anciens.",
          "Ajout d'un basculement entre la vue Km total des releves et la vue Km parcourus cumules depuis 0 pour chaque vehicule.",
        ],
      },
      {
        title: "Carburant et couts",
        items: [
          "Ajout de la repartition des carburants sur l'ensemble des releves du perimetre selectionne.",
          "Ajout d'une legende de donut enrichie avec barres de progression et pourcentage par carburant.",
          "Ajout de la section Evolution du prix du carburant reutilisant le graphique existant par carburant.",
          "Ajout des cartes Depense totale essence et Cout median / 100km.",
          "Ajout des classements Vehicule le moins cher et Vehicule le plus gourmand bases sur les intervalles entre releves.",
          "Conservation de l'affichage compatible Shield Mode pour les valeurs sensibles.",
        ],
      },
      {
        title: "Classements",
        items: [
          "Ajout du Vehicule le plus utilise selon les kilometres parcourus entre releves.",
          "Ajout du Vehicule le moins cher selon le cout median au 100 km.",
          "Ajout du Vehicule le plus gourmand selon la consommation mediane en L/100 km.",
          "Reorganisation des indicateurs en groupes principaux et secondaires avec styles differencies.",
        ],
      },
      {
        title: "Entretiens",
        items: [
          "Extension de l'API statistiques pour recuperer les entretiens realises des vehicules inclus.",
          "Ajout des cartes Depense totale entretiens et Entretiens realises.",
          "Alignement des statistiques d'entretien sur le meme filtre actifs/vendus que les releves.",
        ],
      },
      {
        title: "API et validation",
        items: [
          "Ajout de la route API /api/vehicule/:UtilisateurID/statistiques avec le parametre includeSold.",
          "Aggregation cote backend des vehicules, releves et entretiens realises de l'utilisateur actif.",
          "Validation syntaxique du controleur backend modifie.",
          "Validation du build frontend apres l'ajout de la page, des graphiques et des cartes.",
        ],
      },
    ],
  },
  {
    version: "1.15.3",
    title: "Sauvegarde manuelle superadmin",
    date: "18 juin 2026",
    sections: [
      {
        title: "Administration",
        items: [
          "Ajout d'un bloc Sauvegarde manuelle sur la page Administration, visible uniquement par le superadmin.",
          "Ajout d'un champ de confirmation par mot de passe avant de lancer la sauvegarde.",
          "Affichage d'un retour utilisateur clair lorsque la sauvegarde est creee ou lorsque le mot de passe est incorrect.",
        ],
      },
      {
        title: "Double copie",
        items: [
          "Creation d'une sauvegarde SQL dans uploads/BDD avec un nom date et heure pour eviter d'ecraser une sauvegarde du meme jour.",
          "Telechargement automatique de la meme sauvegarde afin de disposer rapidement d'une copie hors serveur.",
          "Exposition du nom du fichier de sauvegarde dans les headers CORS pour fiabiliser le nom du fichier telecharge.",
        ],
      },
      {
        title: "Securite et backend",
        items: [
          "Ajout d'une route API dediee protegee par JWT et limitee au GradeID superadmin.",
          "Revalidation du mot de passe de l'utilisateur connecte avant l'execution de mysqldump.",
          "Extraction de la logique de sauvegarde dans un service reutilise par la sauvegarde automatique hebdomadaire et la sauvegarde manuelle.",
          "Suppression automatique du fichier incomplet si mysqldump echoue pendant la creation de la sauvegarde.",
        ],
      },
      {
        title: "Validation",
        items: [
          "Validation syntaxique des fichiers backend modifies.",
          "Validation du build frontend apres l'ajout de l'interface de sauvegarde.",
          "Conservation des warnings existants du projet sans ajout de nouveau warning lie aux changements.",
        ],
      },
    ],
  },
  {
    version: "1.15.2",
    title: "Statistiques medianes des releves",
    date: "16 juin 2026",
    sections: [
      {
        title: "Page Vehicule",
        items: [
          "Remplacement de la Conso moyenne par une Conso mediane calculee a partir des consommations entre deux releves.",
          "Remplacement du Cout moyen / 100km par un Cout median / 100km afin de limiter l'impact des releves aberrants.",
          "Remplacement du Km moyen / releve par un Km median / releve sur les cartes de statistiques du vehicule.",
        ],
      },
      {
        title: "Dashboard",
        items: [
          "Alignement des cartes vehicule du Dashboard sur les memes statistiques medianes que la fiche vehicule.",
          "Mise a jour des libelles L/100km median et km median/releve pour afficher clairement la nouvelle methode de calcul.",
          "Conservation du prix moyen par litre, qui reste base sur le rapport cout total / litres totaux.",
        ],
      },
      {
        title: "Robustesse",
        items: [
          "Ajout d'un calcul de mediane reutilise par les statistiques basees sur les intervalles entre releves.",
          "Ignorance des valeurs invalides afin de conserver un affichage fiable lorsqu'un intervalle ne peut pas etre exploite.",
          "Validation du build frontend apres la mise a jour des statistiques et des libelles.",
        ],
      },
    ],
  },
  {
    version: "1.15.1",
    title: "Affichage des entretiens planifies",
    date: "29 mai 2026",
    sections: [
      {
        title: "Page Entretien",
        items: [
          "Ajout d'un defilement horizontal interne sur la section A venir prochainement pour les interfaces etroites.",
          "Protection de la colonne du type d'entretien afin que les libelles comme Revision periodique ne soient plus ecrases.",
          "Ajustement de la largeur minimale de la grille apres l'ajout de la colonne des fichiers.",
        ],
      },
      {
        title: "Echeances",
        items: [
          "Correction de l'affichage date ou kilometrage lorsque seule une des deux donnees est renseignee.",
          "Correction du cas ou un kilometrage absent etait interprete comme 0 km.",
          "Conservation de l'affichage combine date et kilometrage uniquement lorsque les deux valeurs existent.",
        ],
      },
      {
        title: "Validation",
        items: [
          "Validation du build frontend apres les corrections de grille et d'echeance.",
          "Conservation des warnings existants du projet sans ajout de nouveau warning lie aux changements.",
        ],
      },
    ],
  },
  {
    version: "1.15.0",
    title: "Pieces jointes d'entretien securisees",
    date: "29 mai 2026",
    sections: [
      {
        title: "Formulaires Entretien",
        items: [
          "Ajout de pieces jointes multiples sur les entretiens planifies et realises.",
          "Prise en charge des images et PDF avec nom personnalise et categorie par fichier.",
          "Affichage des fichiers existants directement dans le formulaire de modification.",
          "Ajout de la suppression d'un fichier avec modal de confirmation pour eviter les erreurs.",
        ],
      },
      {
        title: "Listes et historique",
        items: [
          "Affichage des pieces jointes dans les entretiens a venir et dans l'historique recent.",
          "Remplacement du badge de fichiers supplementaires par un bouton Voir tout.",
          "Ajout d'une modal listant tous les fichiers avec pagination au-dela de 5 elements.",
          "Ajout d'actions pour ouvrir, telecharger ou supprimer les fichiers depuis les modales.",
        ],
      },
      {
        title: "Apercu securise",
        items: [
          "Ouverture des fichiers dans une modal d'aperçu au lieu d'une nouvelle page.",
          "Affichage integre des images et PDF avec le meme rendu que le lecteur dedie.",
          "Chargement des fichiers via l'API avec JWT et creation d'une URL temporaire uniquement pendant l'ouverture de la modal.",
          "Revocation automatique de l'URL temporaire a la fermeture, a la deconnexion ou au changement d'utilisateur.",
        ],
      },
      {
        title: "API et stockage",
        items: [
          "Ajout du modele EntretienFichier et d'une migration Prisma dediee.",
          "Stockage des fichiers dans uploads/vehicules/UserID/VehiculeID/entretiens selon le type planifie ou realise.",
          "Deplacement automatique des fichiers d'un entretien planifie vers le dossier realise lors du passage en historique.",
          "Suppression physique des fichiers lors de la suppression d'une piece jointe ou d'un entretien.",
        ],
      },
      {
        title: "Securite",
        items: [
          "Blocage de l'acces direct aux pieces jointes d'entretien via les URL publiques /uploads.",
          "Ajout d'une route API protegee pour consulter les fichiers avec verification du JWT.",
          "Verification que le fichier appartient a un entretien lie a un vehicule de l'utilisateur connecte.",
          "Masquage du chemin de stockage dans les reponses de l'API afin de ne pas exposer l'URL backend.",
        ],
      },
      {
        title: "Validation",
        items: [
          "Validation du schema Prisma apres l'ajout du modele de fichiers.",
          "Regeneration du client Prisma pour exposer EntretienFichier.",
          "Validation syntaxique des fichiers backend modifies.",
          "Validation du build frontend apres les ajouts de formulaires, modales et apercu securise.",
        ],
      },
    ],
  },
  {
    version: "1.14.8",
    title: "Formulaire vehicule harmonise",
    date: "28 mai 2026",
    sections: [
      {
        title: "Page Vehicule",
        items: [
          "Refonte de la section Ajouter un nouveau vehicule avec le style sombre des formulaires Releves et Entretien.",
          "Remplacement des listes Type et Marque par des menus deroulants avec recherche.",
          "Ajout d'un resume de saisie et d'une action de reinitialisation harmonisee.",
        ],
      },
    ],
  },
  {
    version: "1.14.7",
    title: "Formulaire de releves harmonise",
    date: "28 mai 2026",
    sections: [
      {
        title: "Page Releves",
        items: [
          "Application du style des champs Ajouter un entretien aux inputs de saisie des releves.",
          "Harmonisation des listes Vehicule et Carburant avec les blocs sombres bordes utilises dans les formulaires d'entretien.",
          "Conservation des pre-remplissages existants du vehicule et de la date depuis la page Vehicule.",
        ],
      },
    ],
  },
  {
    version: "1.14.6",
    title: "Ajout rapide depuis un vehicule",
    date: "28 mai 2026",
    sections: [
      {
        title: "Page Vehicule",
        items: [
          "Remplacement du bouton Ajouter un releve par un menu deroulant Ajouter.",
          "Conservation de l'action Ajouter un releve avec le vehicule et la date du jour pre-remplis.",
          "Ajout d'une action Ajouter un entretien depuis le vehicule selectionne.",
        ],
      },
      {
        title: "Page Entretien",
        items: [
          "Ouverture automatique du modal d'ajout d'entretien via les parametres d'URL.",
          "Pre-remplissage du vehicule et de la date du jour lors de l'arrivee depuis la page Vehicule.",
        ],
      },
    ],
  },
  {
    version: "1.14.5",
    title: "Suppression des releves",
    date: "28 mai 2026",
    sections: [
      {
        title: "Page Vehicule",
        items: [
          "Ajout d'une action Supprimer dans le tableau Derniers releves et dans la liste Tous les releves.",
          "Reprise du style de suppression de l'Historique recent avec bouton icone, tooltip et confirmation sombre.",
          "Blocage de la suppression des releves pendant Shield Mode comme les autres actions d'ecriture.",
        ],
      },
      {
        title: "API",
        items: [
          "Ajout d'un endpoint backend dedie pour supprimer un releve appartenant au vehicule et a l'utilisateur.",
          "Rechargement automatique des donnees du vehicule apres suppression du releve.",
        ],
      },
    ],
  },
  {
    version: "1.14.4",
    title: "Ergonomie des actions et modales",
    date: "28 mai 2026",
    sections: [
      {
        title: "Tooltips accessibles",
        items: [
          "Ajout de tooltips au survol et au focus sur les boutons icones seuls des pages Vehicule et Entretien.",
          "Rendu des tooltips dans document.body afin de rester au-dessus des modales, tableaux et conteneurs avec overflow.",
          "Ajout de aria-label sur les actions icones pour conserver une description accessible au clavier et aux lecteurs d'ecran.",
          "Ajout d'une marge responsive de securite basee sur px-4, sm:px-6 et lg:px-8 afin que les tooltips ne collent pas aux bords de l'ecran.",
        ],
      },
      {
        title: "Actions en icones",
        items: [
          "Passage des actions Ajouter un releve, Marquer vendu et Rehabiliter en boutons icones avec tooltip sur la page Vehicule.",
          "Passage des actions Ajouter un entretien et Realise en boutons icones avec tooltip sur la page Entretien.",
          "Conservation des etats verrouilles par Shield Mode et des titres natifs existants lorsque l'action est bloquee.",
        ],
      },
      {
        title: "Modales Vehicule",
        items: [
          "Correction de la modal Tous les releves afin que le flou d'arriere-plan couvre tout l'ecran, y compris la sidebar.",
          "Correction de la modal Modifier le releve avec le meme rendu au-dessus de la sidebar.",
          "Rendu des modales de releves via portal dans document.body pour eviter les conflits de stacking du layout principal.",
          "Alignement visuel des modales de releves avec le style sombre et le panneau gradient de la modal Modifier l'entretien.",
        ],
      },
      {
        title: "Validation",
        items: [
          "Validation du build frontend apres les ajustements de tooltips, boutons icones et modales.",
          "Conservation des warnings existants du projet sans ajout de nouveau warning lie aux changements.",
        ],
      },
    ],
  },
  {
    version: "1.14.3",
    title: "Dates techniques en base de donnees",
    date: "28 mai 2026",
    sections: [
      {
        title: "Schema Prisma",
        items: [
          "Ajout des colonnes CreateDate et UpdateDate sur les tables qui n'avaient pas encore les deux dates techniques.",
          "Ajout de CreateDate sur Vehicule et Relever, qui possedaient deja UpdateDate.",
          "Conservation de CreateDate avec une valeur par defaut en base afin de renseigner les lignes existantes et les donnees de seed.",
        ],
      },
      {
        title: "Migration MySQL",
        items: [
          "Creation d'une nouvelle migration Prisma pour ajouter les colonnes de dates sur les tables concernees.",
          "Application de la migration sur la base locale vehicle configuree par DATABASE_URL.",
          "Regeneration du client Prisma apres mise a jour du schema.",
        ],
      },
      {
        title: "Ecritures serveur",
        items: [
          "Ajout explicite de l'heure serveur lors des creations avec CreateDate dans les controleurs vehicules, releves et entretiens.",
          "Ajout explicite de l'heure serveur lors des modifications avec UpdateDate dans les controleurs et le repository utilisateur.",
          "Extension des ecritures d'administration des donnees de reference pour renseigner CreateDate et UpdateDate.",
        ],
      },
      {
        title: "Exports et validation",
        items: [
          "Ajout de CreateDate dans les details vehicule et releves renvoyes par l'API lorsque ces donnees sont utiles.",
          "Ajout de CreateDate dans les exports CSV vehicule.csv et releves.csv des archives de vehicule.",
          "Validation du schema Prisma et verification syntaxique des fichiers backend modifies.",
        ],
      },
    ],
  },
  {
    version: "1.14.2",
    title: "Shield Mode sur la page Entretien",
    date: "28 mai 2026",
    sections: [
      {
        title: "Confidentialite niveau 1",
        items: [
          "Application du mode Shield leger sur les donnees d'entretien avec dates reduites a l'annee.",
          "Arrondi des kilometres et des couts afin de conserver une lecture utile sans exposer les valeurs exactes.",
          "Conservation de l'affichage des listes, du calendrier, des statistiques et de la repartition des couts pendant Shield Mode.",
        ],
      },
      {
        title: "Confidentialite niveau 2",
        items: [
          "Remplacement des donnees sensibles par des etoiles en Shield Mode niveau 2 sur les couts, dates, kilometres, garages et notes.",
          "Masquage des notes dans les cartes, l'historique recent et la modal du calendrier lorsque le niveau 2 est actif.",
          "Correction du formatage des couts afin d'eviter une erreur navigateur lors de l'activation du niveau 2.",
        ],
      },
      {
        title: "Verrouillage des actions",
        items: [
          "Blocage de l'ajout, de la modification, de la suppression et du passage en realise pendant Shield Mode.",
          "Fermeture automatique des modales d'ecriture ou de suppression lorsqu'un mode Shield est active.",
          "Validation du build frontend apres l'alignement des regles Shield de la page Entretien.",
        ],
      },
    ],
  },
  {
    version: "1.14.1",
    title: "Telechargement direct des archives",
    date: "28 mai 2026",
    sections: [
      {
        title: "Page Vehicule",
        items: [
          "Ajout d'un bouton Telecharger les archives du vehicule dans la liste des actions en haut de la page Vehicule.",
          "Placement du bouton avec les actions existantes comme Ajouter un releve, Marquer vendu, Modifier et Supprimer.",
          "Conservation du verrouillage par Shield Mode afin de bloquer l'action lorsque les donnees sensibles sont protegees.",
        ],
      },
      {
        title: "Archive sans suppression",
        items: [
          "Reutilisation du meme modal de mot de passe que le telechargement avant suppression.",
          "Reutilisation du meme endpoint backend pour generer le ZIP du vehicule et les exports CSV.",
          "Telechargement de l'archive sans afficher la confirmation de suppression et sans supprimer le vehicule.",
        ],
      },
      {
        title: "Compatibilite",
        items: [
          "Conservation du parcours Telecharger avant suppression dans le modal de suppression.",
          "Retour vers la validation finale de suppression uniquement lorsque le telechargement vient du parcours de suppression.",
          "Validation du build frontend apres la modification.",
        ],
      },
    ],
  },
  {
    version: "1.14.0",
    title: "Suppression et export des vehicules",
    date: "28 mai 2026",
    sections: [
      {
        title: "Suppression definitive",
        items: [
          "Ajout d'un bouton Supprimer sur la page Vehicule, place avec les actions Marquer vendu et Modifier.",
          "Ajout d'un premier modal de confirmation dans le style sombre existant de l'application.",
          "Ajout d'un second modal de validation demandant le mot de passe avant suppression definitive.",
          "Redirection automatique vers le Dashboard apres suppression avec notification de validation.",
        ],
      },
      {
        title: "Suppression en cascade",
        items: [
          "Ajout d'une route backend dediee pour supprimer un vehicule appartenant a l'utilisateur.",
          "Suppression en cascade des releves, entretiens planifies et entretiens realises lies au vehicule.",
          "Suppression physique des données du vehicule lorsque celle-ci existe dans le dossier uploads/vehicules.",
          "Protection de l'image par defaut afin qu'elle ne soit jamais supprimee lors de la suppression d'un vehicule.",
        ],
      },
      {
        title: "Archive avant suppression",
        items: [
          "Ajout d'une option Telecharger avant suppression dans le modal de confirmation.",
          "Demande du mot de passe avant de generer et telecharger l'archive du dossier du vehicule.",
          "Generation d'un fichier ZIP contenant les fichiers physiques du dossier uploads/vehicules de ce vehicule.",
          "Retour automatique vers le modal final de suppression apres le telechargement si l'utilisateur souhaite continuer.",
        ],
      },
      {
        title: "Donnees brutes CSV",
        items: [
          "Ajout d'un sous-dossier donneesBrute dans l'archive ZIP du vehicule.",
          "Generation des exports vehicule.csv, releves.csv, entretiens_planifies.csv et entretiens_realises.csv.",
          "Export limite au vehicule concerne afin de ne pas melanger les donnees avec celles d'autres vehicules.",
          "Remplacement des cles etrangeres par des noms lisibles comme carburant, marque, type, etat, utilisateur, type d'entretien et categorie.",
        ],
      },
      {
        title: "Securite et feedback",
        items: [
          "Validation backend du mot de passe pour le telechargement d'archive et la suppression definitive.",
          "Ajout d'un bridage partage entre telechargement et suppression : 3 essais incorrects puis blocage pendant 5 minutes.",
          "Affichage du feedback sous le champ mot de passe avec le nombre d'essais restants ou le temps d'attente.",
          "Remise a zero du compteur d'essais apres une validation reussie du mot de passe.",
        ],
      },
    ],
  },
  {
    version: "1.13.0",
    title: "Gestion des donnees de reference",
    date: "27 mai 2026",
    sections: [
      {
        title: "Administration",
        items: [
          "Ajout d'une nouvelle section Donnees de reference dans la page Administration.",
          "Ajout de la gestion des types de vehicule, marques de vehicule, carburants, categories d'entretien et entretiens types.",
          "Ajout d'un formulaire unique pour ajouter ou modifier les elements selon la table selectionnee.",
          "Conservation du style sombre existant de l'administration avec navigation par table, compteurs et actions rapides.",
        ],
      },
      {
        title: "Recherche et pagination",
        items: [
          "Ajout d'une barre de recherche par table afin de verifier rapidement si un contenu existe deja.",
          "Recherche locale sur les champs utiles : ID, nom, couleur, description et categorie selon la table.",
          "Ajout d'une pagination limitee a 10 elements par page sur les tables de reference.",
          "Reprise du style de pagination existant avec boutons precedent, suivant, pages numerotees et ellipses.",
        ],
      },
      {
        title: "API admin",
        items: [
          "Ajout d'endpoints backend dedies sous /api/admin/reference-data pour charger toutes les donnees de reference.",
          "Ajout des actions create, update et delete pour chaque table de reference geree depuis l'administration.",
          "Protection des nouvelles routes avec authentification admin et middleware Shield Mode.",
          "Gestion des suppressions bloquees lorsqu'un element est encore utilise par une relation en base.",
        ],
      },
      {
        title: "Robustesse",
        items: [
          "Validation des champs obligatoires avant creation ou modification.",
          "Verification de l'existence de la categorie avant de creer ou modifier un type d'entretien.",
          "Rechargement automatique des donnees apres ajout, modification ou suppression.",
          "Affichage de messages d'erreur explicites lorsque l'action ne peut pas etre effectuee.",
        ],
      },
    ],
  },
  {
    version: "1.12.0",
    title: "Ameliorations de la page Entretien",
    date: "27 mai 2026",
    sections: [
      {
        title: "Listes et pagination",
        items: [
          "Ajout d'une pagination locale a 5 elements par page sur A venir prochainement et Historique recent.",
          "Reorganisation de la page Entretien avec A venir prochainement en pleine largeur, Calendrier d'entretien et Repartition des couts sur la deuxieme ligne, puis Historique recent en pleine largeur.",
          "Ajout d'un overflow horizontal fiable sur le tableau Historique recent pour une lecture correcte sur telephone.",
          "Correction de l'affichage des echeances planifiees afin de ne montrer que la date ou le kilometrage disponible, sauf lorsque les deux valeurs sont renseignees.",
        ],
      },
      {
        title: "Notes et historique",
        items: [
          "Ajout de l'affichage des notes dans A venir prochainement avec un bouton Voir la note lorsque la note existe.",
          "Ajout des colonnes Garage et Note dans Historique recent.",
          "Ajout d'un modal de lecture des notes pour les entretiens planifies et realises.",
          "Conservation des boutons de note uniquement lorsqu'une note est presente afin d'eviter des actions inutiles.",
        ],
      },
      {
        title: "Suppression d'entretiens",
        items: [
          "Ajout d'un bouton de suppression sur les entretiens planifies et realises en cas de creation par erreur.",
          "Ajout d'un modal de confirmation avant suppression afin d'eviter les suppressions accidentelles.",
          "Ajout des endpoints backend DELETE pour supprimer un entretien planifie ou realise.",
          "Protection des nouvelles routes de suppression avec le middleware Shield Mode existant.",
        ],
      },
      {
        title: "Calendrier",
        items: [
          "Transformation des jours du calendrier contenant un entretien en boutons cliquables.",
          "Coloration complete du bouton du jour selon le statut prioritaire : a venir, en retard ou realise.",
          "Ajout d'un modal Calendrier listant les entretiens du jour avec vehicule, type, statut, echeance, cout, garage et note selon les donnees disponibles.",
          "Suppression des anciens points de couleur sous les jours au profit d'une interaction plus lisible.",
        ],
      },
      {
        title: "Couts",
        items: [
          "Passage de la Repartition des couts du filtre 12 mois au total general de tous les entretiens realises.",
          "Mise a jour de la carte Coût total pour afficher le total general et le libelle Tous les entretiens realises.",
          "Conservation du total 12 mois dans les statistiques backend pour compatibilite, tout en exposant le nouveau total global.",
        ],
      },
    ],
  },
  {
    version: "1.11.1",
    title: "Seed des entretiens",
    date: "26 mai 2026",
    sections: [
      {
        title: "Seed Prisma",
        items: [
          "Ajout des categories d'entretien par defaut dans prisma/seed.js : Entretien courant, Pneus, Freinage, Fluides, Transmission et Autres.",
          "Ajout des 13 types d'entretien par defaut relies a leur categorie respective.",
          "Utilisation d'une logique findFirst puis update/create pour eviter les doublons lorsque le seed est relance.",
          "Mise a jour des categories et types existants si leur couleur, icone, description ou categorie change.",
        ],
      },
      {
        title: "Robustesse",
        items: [
          "Ajout d'une verification explicite des categories avant la creation des types d'entretien.",
          "Ajout de skipDuplicates sur les utilisateurs par defaut afin d'eviter le blocage du script sur les surnoms uniques.",
          "Conservation des donnees deja presentes sans duplication des nouvelles tables d'entretien.",
        ],
      },
    ],
  },
  {
    version: "1.11.0",
    title: "Gestion des entretiens",
    date: "26 mai 2026",
    sections: [
      {
        title: "Base de donnees",
        items: [
          "Ajout des tables CategorieEntretien, EntretienType, EntretienPlanifie et EntretienRealise dans Prisma.",
          "Ajout des relations entre les vehicules, les entretiens planifies, les entretiens realises et les types d'entretien.",
          "Creation des migrations MySQL pour les nouvelles tables et les donnees de depart des categories et types d'entretien.",
          "Ajout de 6 categories et 13 types d'entretien par defaut pour initialiser la gestion d'entretien.",
        ],
      },
      {
        title: "Page Entretien",
        items: [
          "Ajout de la route /entretien et activation du lien Entretien dans la sidebar.",
          "Creation d'une page Entretien inspiree du visuel de reference avec statistiques, liste des entretiens a venir, calendrier, historique recent et repartition des couts.",
          "Connexion de la page aux vraies donnees de la base via une nouvelle API d'overview.",
          "Calcul des entretiens a venir, en retard, realises, du cout total sur 12 mois et de la repartition par categorie.",
        ],
      },
      {
        title: "Ajout d'entretien",
        items: [
          "Ajout d'une modal sombre pour planifier un entretien a venir ou enregistrer un entretien deja realise.",
          "Preselection et affichage des vehicules avec image, marque, modele et type.",
          "Ajout des champs categorie, type d'entretien, date, kilometrage, priorite, cout, garage et notes selon le mode choisi.",
          "Remplacement des selects natifs par des dropdowns custom avec recherche, icones et couleurs coherentes avec le reste de l'interface.",
        ],
      },
      {
        title: "Edition et historique",
        items: [
          "Ajout de la modification des entretiens planifies depuis la section A venir prochainement.",
          "Ajout de la modification des entretiens realises depuis la section Historique recent.",
          "Ajout de l'action Realise pour transformer un entretien planifie en entretien realise dans l'historique.",
          "L'action Realise lie l'historique cree a l'entretien planifie d'origine et marque ce dernier comme realise.",
        ],
      },
      {
        title: "API",
        items: [
          "Ajout des endpoints backend pour charger l'overview entretien, creer un entretien, modifier un entretien planifie, modifier un entretien realise et finaliser un entretien planifie.",
          "Protection des routes d'ecriture entretien avec le middleware Shield Mode existant.",
          "Regeneration du client Prisma et validation du schema apres l'ajout des nouveaux modeles.",
        ],
      },
    ],
  },
  {
    version: "1.10.1",
    title: "Correctif du tableau des relevés",
    date: "26 mai 2026",
    sections: [
      {
        title: "Releves",
        items: [
          "Masquage de la colonne Inclinaison dans le tableau Derniers releves lorsqu'aucune donnee d'inclinaison n'est disponible sur les lignes affichees.",
          "Conservation de la colonne Inclinaison des qu'au moins un releve affiche possede une inclinaison gauche ou droite.",
          "Reutilisation du meme controle de disponibilite pour garder un comportement coherent avec la carte Inclinaisons par releve.",
        ],
      },
    ],
  },
  {
    version: "1.10.0",
    title: "Shield Mode niveau 2 et verrouillage backend",
    date: "26 mai 2026",
    sections: [
      {
        title: "Shield Mode niveau 2",
        items: [
          "Ajout d'un niveau 2 de Shield Mode accessible depuis la modal de desactivation du niveau 1, sans demande de mot de passe supplementaire.",
          "Conservation de la demande de mot de passe uniquement pour desactiver Shield Mode, quel que soit le niveau actif.",
          "Bascule des accents visuels du niveau 2 vers un theme rouge afin de distinguer clairement le mode de confidentialite renforce.",
        ],
      },
      {
        title: "Confidentialite renforcee",
        items: [
          "Masquage des caracteres alphanumeriques du modele vehicule avec des etoiles en Shield Mode niveau 2.",
          "Arrondi des kilometres a la centaine, des prix a l'euro, des consommations au litre et des dates a l'annee dans les donnees accessibles.",
          "Blocage de la deconnexion pendant Shield Mode afin d'eviter une sortie de session depuis un etat confidentiel.",
        ],
      },
      {
        title: "Graphiques confidentiels",
        items: [
          "Lissage des courbes en Shield Mode niveau 2 avec une moyenne glissante afin de reduire la precision visuelle des donnees.",
          "Affichage des dates des graphiques au format mois + annee pendant Shield Mode niveau 2.",
          "Arrondi des prix et des consommations des graphiques a une decimale pendant Shield Mode niveau 2.",
        ],
      },
      {
        title: "Protection backend",
        items: [
          "Ajout d'un header X-Shield-Mode-Level envoye par les appels API Axios et fetch du frontend.",
          "Ajout d'un middleware backend Shield Mode qui verrouille les routes sensibles lorsque Shield Mode est actif.",
          "Maintien des routes de lecture vehicule accessibles pendant Shield Mode afin que les donnees utiles restent disponibles apres un rafraichissement de page.",
          "Blocage backend des actions d'ecriture comme ajouter un vehicule, ajouter un releve, modifier un vehicule, modifier un releve, vendre ou rehabiliter un vehicule.",
        ],
      },
      {
        title: "Application",
        items: [
          "Correction du middleware d'authentification backend pour respecter le format async attendu par Fastify.",
        ],
      },
    ],
  },
  {
    version: "1.9.0",
    title: "Shield Mode et confidentialite",
    date: "23 mai 2026",
    sections: [
      {
        title: "Shield Mode",
        items: [
          "Ajout d'un toggle Shield Mode dans le header, place a gauche du bouton Notifications.",
          "Activation immediate du mode confidentialite et desactivation protegee par une modal demandant le mot de passe utilisateur.",
          "Conservation d'un alias Drive-[ID court] pendant toute la session verrouillee, jusqu'au deverrouillage du mode.",
        ],
      },
      {
        title: "Donnees masquees",
        items: [
          "Remplacement du nom utilisateur par l'alias Shield Mode dans le header, la sidebar, le menu profil et la page Profil.",
          "Remplacement de l'image de profil par l'image publique confidentialiter.png.",
          "Masquage des immatriculations avec conservation du format, par exemple ER-738-BA devient **-***-**.",
          "Masquage du kilometrage total, des kilometres declares, des dates de releves et de mises a jour.",
          "Masquage des depenses totales, du prix total des releves et des couts calcules dans la page Simulation.",
        ],
      },
      {
        title: "Acces et actions",
        items: [
          "Blocage de l'acces aux pages Parametres, Releves et Administration pendant Shield Mode.",
          "Desactivation des boutons sensibles comme Ajouter un vehicule, Ajouter un releve, Modifier, Marquer vendu et Rehabiliter.",
          "Fermeture automatique des modes edition et modales de modification lorsque Shield Mode est active.",
        ],
      },
      {
        title: "Interface",
        items: [
          "Bascule des accents visuels du theme du bleu vers le vert pendant Shield Mode.",
          "Ajout d'un contexte frontend dedie pour centraliser l'etat, l'alias, l'image confidentielle et les helpers de masquage.",
        ],
      },
    ],
  },
  {
    version: "1.8.0",
    title: "Gestion des vehicules vendus",
    date: "23 mai 2026",
    sections: [
      {
        title: "Navigation",
        items: [
          "Ajout d'une section Actifs et d'une section Vendus dans le dropdown Vehicules de la sidebar.",
          "Les vehicules vendus restent accessibles depuis la sidebar afin de consulter leur fiche et leurs donnees historiques.",
          "La recherche du dropdown filtre maintenant les vehicules actifs et vendus.",
        ],
      },
      {
        title: "Vehicules vendus",
        items: [
          "Les vehicules vendus ne sont pas affiches dans le Dashboard, qui reste dedie aux vehicules actifs.",
          "La page Vehicule peut maintenant ouvrir une fiche de vehicule vendu avec ses informations et ses releves existants.",
          "Ajout d'un bouton Rehabiliter sur les fiches de vehicules vendus pour les remettre en actif en cas d'erreur.",
        ],
      },
      {
        title: "Releves",
        items: [
          "Masquage du bouton Ajouter un releve lorsqu'un vehicule est vendu.",
          "Conservation de l'acces en lecture et correction aux releves deja enregistres pour les vehicules vendus.",
        ],
      },
      {
        title: "Application",
        items: [
          "Ajout de l'endpoint backend permettant de rebasculer un vehicule vendu en actif.",
        ],
      },
    ],
  },
  {
    version: "1.7.0",
    title: "Harmonisation visuelle de l'application",
    date: "23 mai 2026",
    sections: [
      {
        title: "Design system",
        items: [
          "Harmonisation des pages Releves, Parametres, Administration et Mises a jour avec le style sombre des pages Dashboard et Vehicule.",
          "Generalisation des cartes slate avec bordures sky discretes, titres sobres, champs harmonises et boutons coherents.",
          "Ajout d'icones Heroicons sur les actions principales et les zones fonctionnelles.",
        ],
      },
      {
        title: "Releves",
        items: [
          "Refonte de la page d'ajout de releve avec une carte principale, une zone d'affectation et un resume de saisie.",
          "Mise a jour des listes Vehicule et Carburant pour reprendre le style des formulaires recents.",
          "Ajout d'une action de reinitialisation coherente avec le reste de l'interface.",
        ],
      },
      {
        title: "Parametres",
        items: [
          "Refonte de la page Parametres avec une carte Profil et une carte Zone danger.",
          "Modernisation du formulaire de profil, des messages de succes/erreur et des modales de suppression de compte.",
          "Correction d'attributs JSX invalides dans les controles de suppression d'image.",
        ],
      },
      {
        title: "Administration",
        items: [
          "Refonte de la page Administration avec une grille claire pour creation admin, liste admins et utilisateurs par etat.",
          "Modernisation du tableau des administrateurs avec badges d'etat et actions plus lisibles.",
          "Refonte des cartes utilisateurs actifs, bloques et supprimes avec avatars, badges et boutons harmonises.",
        ],
      },
      {
        title: "Application",
        items: [
          "Refonte de cette page Mises a jour pour reprendre le meme design que les autres pages modernisees.",
        ],
      },
    ],
  },
  {
    version: "1.6.0",
    title: "Dashboard des vehicules",
    date: "23 mai 2026",
    sections: [
      {
        title: "Dashboard",
        items: [
          "Ajout de la page Dashboard avec une section cliquable par vehicule actif.",
          "Affichage en lecture seule des informations principales de chaque vehicule.",
          "Calcul et affichage des kilometres declares, de la consommation moyenne, du prix moyen par litre et des kilometres moyens par releve.",
          "Chaque section Dashboard ouvre directement la fiche du vehicule selectionne.",
        ],
      },
      {
        title: "Navigation",
        items: [
          "Separation de la page Dashboard et de la page Vehicule afin de conserver l'edition sur la fiche vehicule.",
          "Ajout du lien Dashboard dans la sidebar avant le dropdown Vehicules.",
        ],
      },
      {
        title: "Application",
        items: [
          "Affichage du nom et de la version de l'application depuis les variables d'environnement.",
        ],
      },
    ],
  },
  {
    version: "1.5.0",
    title: "Statistiques vehicule et responsive",
    date: "22 mai 2026",
    sections: [
      {
        title: "Statistiques",
        items: [
          "Ajout d'une premiere rangee de cartes basee sur le dernier releve avec comparaison au releve precedent.",
          "Ajout d'une deuxieme rangee de cartes basee sur les moyennes generales du vehicule.",
          "Calcul global de la consommation moyenne, du cout moyen / 100km, du prix moyen par litre et des kilometres moyens par releve.",
          "La card d'identite affiche maintenant les kilometres declares au lieu du kilometrage total du compteur.",
        ],
      },
      {
        title: "Kilometrage",
        items: [
          "Ajout des kilometres declares sur la card Kilometrage, calcules entre le premier et le dernier releve du vehicule.",
          "Conservation du kilometrage total compteur dans le sous-titre du graphique Kilometrage.",
        ],
      },
      {
        title: "Responsive",
        items: [
          "Correction des debordements a partir de la section Kilometrage sur mobile et desktop.",
          "Correction de la ligne Derniers releves / Activite recente pour mieux utiliser l'espace disponible sur desktop.",
          "Ajout d'un overflow horizontal fiable au tableau des releves sur mobile.",
        ],
      },
      {
        title: "Interface",
        items: [
          "Correction du fond bleu de l'application afin qu'il reste fixe pendant le scroll.",
        ],
      },
    ],
  },
  {
    version: "1.4.0",
    title: "Edition des vehicules et des releves",
    date: "22 mai 2026",
    sections: [
      {
        title: "Vehicules",
        items: [
          "Ajout d'un bouton Modifier sur la page vehicule pour transformer la card d'identite en formulaire d'edition.",
          "Edition du nom, modele, immatriculation, date de premiere immatriculation, type et marque depuis la fiche vehicule.",
          "Ajout d'une colonne UpdateDate sur les vehicules afin de suivre la derniere mise a jour de leur fiche.",
          "L'activite recente affiche maintenant une ligne dediee a la derniere mise a jour du vehicule basee uniquement sur Vehicule.UpdateDate.",
        ],
      },
      {
        title: "Photos vehicule",
        items: [
          "Ajout d'une colonne Photo sur les vehicules pour stocker le chemin de l'image.",
          "Ajout de l'upload d'image dans le dossier uploads/vehicules par utilisateur et par vehicule.",
          "Suppression automatique de l'ancienne photo lors du remplacement.",
          "Ajout d'une option pour supprimer la photo actuelle et revenir a l'image par defaut de l'application.",
        ],
      },
      {
        title: "Releves",
        items: [
          "Ajout d'une colonne Editions dans le tableau Derniers releves.",
          "Ajout d'une modal de correction pour modifier un releve existant en cas d'erreur de saisie.",
          "Edition de la date, carburant, kilometrage, prix par litre, prix total, litres, consommation et inclinaisons.",
          "Ajout d'une colonne UpdateDate sur les releves afin d'identifier le dernier releve corrige.",
        ],
      },
      {
        title: "Activite recente",
        items: [
          "Ajout d'une ligne Dernier releve mis a jour basee sur le dernier Relever.UpdateDate disponible.",
          "Separation claire entre la mise a jour de la fiche vehicule et la mise a jour d'un releve.",
        ],
      },
      {
        title: "Interface",
        items: [
          "Deplacement des controles d'image dans la colonne de droite du mode edition vehicule.",
          "Masquage du statut Actif/Vendu et des statistiques de la card uniquement pendant l'edition pour liberer de l'espace.",
        ],
      },
    ],
  },
  {
    version: "1.3.0",
    title: "Refonte complete de la page vehicule",
    date: "21 mai 2026",
    sections: [
      {
        title: "Page vehicule",
        items: [
          "Refonte visuelle complete de la page /vehicule avec une presentation dashboard sombre inspiree du visuel de reference.",
          "Ajout d'un hero vehicule avec image generique par defaut lorsque le vehicule ne possede pas encore d'image.",
          "Suppression de l'ancienne barre d'onglets des vehicules au profit de la selection depuis le dropdown Vehicules de la sidebar.",
          "Correction de la selection du vehicule afin que les donnees affichees correspondent toujours au parametre vehicleId de l'URL.",
        ],
      },
      {
        title: "Statistiques",
        items: [
          "Les cartes Conso moyenne, Cout moyen / 100km, Depense et Kilometrage se basent maintenant sur le dernier releve avec comparaison au releve precedent.",
          "La consommation moyenne utilise le calcul reel litres / distance plutot que la valeur de l'ordinateur de bord.",
          "Les graphiques utilisent un filtre par nombre de releves : 7, 30, 90 ou Tout.",
          "La carte Inclinaisons par releve est masquee quand aucune donnee d'inclinaison n'est disponible, et l'espace est donne au graphique Kilometrage.",
        ],
      },
      {
        title: "Graphiques",
        items: [
          "Ajout d'une presentation compacte et sombre pour les graphiques de consommation, prix carburant, kilometrage, carburants et inclinaisons.",
          "Transformation de la repartition des carburants en donut avec une icone pompe a essence centree dans le donut.",
          "Suppression de la section temperature, meteo et carburant le moins cher sur la page vehicule.",
        ],
      },
      {
        title: "Releves",
        items: [
          "Ajout d'un tableau Derniers releves sur la page vehicule.",
          "Ajout d'un modal Tous les releves avec table paginee a 10 releves par page.",
          "Le bouton Ajouter un releve depuis une fiche vehicule preselectionne le vehicule d'origine et pre-remplit la date du jour.",
          "La page d'ajout de releve utilise maintenant la date du jour par defaut.",
        ],
      },
      {
        title: "Navigation et interface",
        items: [
          "Ajout du bouton Ajouter un vehicule directement dans le dropdown Vehicules de la sidebar.",
          "Correction du fond general pour qu'il reste coherent pendant le scroll.",
          "Mise a jour de l'activite recente pour afficher la derniere mise a jour disponible, actuellement le dernier releve d'essence.",
        ],
      },
    ],
  },
  {
    version: "1.2.0",
    title: "Refonte de la navigation",
    date: "21 mai 2026",
    sections: [
      {
        title: "Navigation",
        items: [
          "Remplacement de l'ancienne navbar horizontale par une sidebar fixe sur desktop.",
          "Ajout d'un menu burger sur mobile pour acceder a la navigation principale.",
          "Ajout d'un dropdown Vehicules avec recherche, affiche a droite sur desktop et sous le bouton sur mobile.",
          "Ajout du lien Mises a jour en bas de la sidebar.",
        ],
      },
      {
        title: "Vehicules",
        items: [
          "Ajout d'une route /vehicule avec selection automatique du vehicule via le parametre vehicleId.",
          "Les liens du dropdown Vehicules ouvrent directement la page vehicule avec le vehicule selectionne.",
        ],
      },
      {
        title: "Interface",
        items: [
          "Ajout du nouveau logo Vehicle inspire du visuel de reference.",
          "Ajout du header de page avec le message Bonjour et l'etat de la section actuelle.",
          "Affichage de la version de l'application depuis la variable REACT_APP_VER.",
        ],
      },
    ],
  },
  {
    version: "1.1.0",
    title: "Ajout de la page des mises a jour",
    date: "21 mai 2026",
    sections: [
      {
        title: "Application",
        items: [
          "Ajout d'une nouvelle page Mises a jour pour suivre les changements importants de Vehicle.",
          "Creation d'un historique clair des versions afin de mieux identifier les evolutions de l'application.",
          "La version 1.0.0 correspond a la version actuelle avant l'ajout de cette page.",
        ],
      },
      {
        title: "Navigation",
        items: [
          "Ajout d'un acces a la page des mises a jour depuis le menu utilisateur.",
          "Ajout de la route protegee /updates pour consulter l'historique une fois connecte.",
        ],
      },
      {
        title: "Interface",
        items: [
          "Mise en place d'une presentation par version avec titre, date, sections et liste des changements.",
          "Ajout d'un style responsive coherent avec l'interface existante.",
        ],
      },
    ],
  },
];

function UpdateCard({ update }) {
  return (
    <article className="overflow-hidden rounded-lg border border-sky-500/20 bg-slate-950/60 shadow-[0_18px_55px_rgba(2,6,23,0.28)]">
      <div className="border-b border-white/10 px-5 py-4 sm:px-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="inline-flex items-center gap-2 text-xs font-semibold uppercase text-sky-300">
              <PaintBrushIcon className="h-4 w-4" />
              Version {update.version}
            </p>
            <h2 className="mt-1 text-xl font-semibold text-slate-50">
              {update.title}
            </h2>
          </div>
          <time className="inline-flex items-center gap-2 text-sm text-slate-400">
            <ClockIcon className="h-4 w-4" />
            {update.date}
          </time>
        </div>
      </div>

      <div className="grid gap-5 p-5 sm:p-6 md:grid-cols-2">
        {update.sections.map((section) => (
          <section key={section.title} className="rounded-lg border border-white/10 bg-slate-900/50 p-4">
            <h3 className="text-sm font-semibold text-slate-50">
              {section.title}
            </h3>
            <ul className="mt-3 grid gap-2 text-sm leading-6 text-slate-300">
              {section.items.map((item) => (
                <li key={item} className="flex gap-3">
                  <CheckCircleIcon className="mt-1 h-4 w-4 shrink-0 text-sky-300" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </article>
  );
}

export default function UpdatePage() {
  const latestUpdate = updates[0];
  const latestUpdates = updates.slice(0, 3);
  const historicalUpdates = updates.slice(3);

  return (
    <main className="mx-auto max-w-7xl grow">
      <header className="mb-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-slate-50">Mises a jour</h1>
            <p className="mt-1 text-sm text-slate-400">
              Historique des changements importants apportes a l'application.
            </p>
          </div>
          <span className="inline-flex items-center gap-2 rounded border border-sky-500/20 bg-slate-900/70 px-3 py-1.5 text-xs font-semibold text-slate-200">
            <TagIcon className="h-4 w-4 text-sky-300" />
            {appName} v{appVersion}
          </span>
        </div>
      </header>

      <section className="mb-8 overflow-hidden rounded-lg border border-sky-500/30 bg-[radial-gradient(circle_at_20%_10%,rgba(14,165,233,0.16),transparent_30%),linear-gradient(135deg,rgba(2,6,23,0.98),rgba(15,23,42,0.9))] shadow-[0_24px_80px_rgba(2,6,23,0.38)]">
        <div className="grid gap-6 p-5 sm:p-6 lg:grid-cols-[minmax(0,1fr)_minmax(260px,0.35fr)]">
          <div>
            <p className="text-xs font-semibold uppercase text-sky-300">Derniere version</p>
            <h2 className="mt-2 text-3xl font-semibold text-slate-50">{latestUpdate.title}</h2>
            <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-300">
              La version {latestUpdate.version} {latestUpdateResume}
              </p>
          </div>
          <div className="rounded-lg border border-sky-500/20 bg-slate-950/60 p-5">
            <div className="flex items-center gap-3">
              <span className="grid h-10 w-10 place-items-center rounded-full bg-sky-500/15 text-sky-300">
                <SparklesIcon className="h-5 w-5" />
              </span>
              <div>
                <p className="text-sm font-semibold text-slate-50">Version {latestUpdate.version}</p>
                <p className="mt-1 text-xs text-slate-400">{latestUpdate.date}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="space-y-8">
        <section>
          <div className="mb-4">
            <h2 className="text-lg font-semibold text-slate-50">Dernieres mises a jour</h2>
            <p className="mt-1 text-sm text-slate-400">Les trois versions les plus recentes.</p>
          </div>
          <div className="space-y-6">
            {latestUpdates.map((update) => (
              <UpdateCard key={update.version} update={update} />
            ))}
          </div>
        </section>

        <section>
          <details className="group overflow-hidden rounded-lg border border-sky-500/20 bg-slate-950/60 shadow-[0_18px_55px_rgba(2,6,23,0.28)]">
            <summary className="flex cursor-pointer list-none items-center justify-between gap-4 px-5 py-4 sm:px-6">
              <div>
                <h2 className="text-lg font-semibold text-slate-50">Historique complet</h2>
                <p className="mt-1 text-sm text-slate-400">
                  {historicalUpdates.length} version{historicalUpdates.length > 1 ? "s" : ""} plus ancienne{historicalUpdates.length > 1 ? "s" : ""}, de v{historicalUpdates[0]?.version} a v{historicalUpdates[historicalUpdates.length - 1]?.version}.
                </p>
              </div>
              <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-sky-500/20 text-sky-300 transition group-open:rotate-180">
                <ChevronDownIcon className="h-5 w-5" />
              </span>
            </summary>
            <div className="space-y-6 border-t border-white/10 p-5 sm:p-6">
              {historicalUpdates.map((update) => (
                <UpdateCard key={update.version} update={update} />
              ))}
            </div>
          </details>
        </section>
      </div>
    </main>
  );
}
